'use strict';

/**
 * Utility for macronutrient percentage calculations and adjustments
 * Handles the smart adjustment of macro percentages to ensure they sum to 100%
 */

// Constants
export const CALORIES_PER_GRAM = {
    protein: 4, // nutrient 203
    fat: 9, // nutrient 204
    carbs: 4, // nutrient 205
};

const ROUNDING_ERROR_TOLERANCE = {
    MIN: 99,
    MAX: 101
};

/**
 * Calculates the percentage of a macronutrient based on grams and total calories
 * @param {number} grams - Amount in grams
 * @param {number} totalCalories - Total calories
 * @param {number} caloriesPerGram - Calories per gram of nutrient
 * @returns {number} Percentage (0-100)
 */
export const calculatePercentageFromGrams = (grams, totalCalories, caloriesPerGram) => {
    if (!totalCalories || !grams) return 0;
    return ((grams * caloriesPerGram) / totalCalories) * 100;
};

/**
 * Calculates grams based on percentage and total calories
 * @param {number} percentage - Percentage (0-100)
 * @param {number} totalCalories - Total calories
 * @param {number} caloriesPerGram - Calories per gram of nutrient
 * @returns {number} Amount in grams
 */
export const calculateGramsFromPercentage = (percentage, totalCalories, caloriesPerGram) => {
    if (!totalCalories || !percentage) return 0;
    return ((percentage / 100) * totalCalories) / caloriesPerGram;
};

/**
 * Ensures macro percentages sum to exactly 100%
 * Adjusts the largest value to compensate for rounding errors
 * @param {number} protein - Protein percentage
 * @param {number} fat - Fat percentage
 * @param {number} carbs - Carbohydrates percentage
 * @returns {Object} Adjusted percentages {protein, fat, carbs}
 */
export const ensureTotalIs100 = (protein, fat, carbs) => {
    // If any value is invalid, return without adjustment
    if (isNaN(protein) || isNaN(fat) || isNaN(carbs)) {
        return { protein, fat, carbs };
    }

    const total = protein + fat + carbs;

    // If already sums to 100, do nothing
    if (total === 100) {
        return { protein, fat, carbs };
    }

    // Calculate the difference
    const diff = 100 - total;

    // Create array with macros and their values
    const macros = [
        { name: 'protein', value: protein },
        { name: 'fat', value: fat },
        { name: 'carbs', value: carbs },
    ];

    // Sort by value (largest first)
    macros.sort((a, b) => b.value - a.value);

    // Adjust the largest value
    macros[0].value += diff;

    // Ensure no value is negative
    if (macros[0].value < 0) {
        macros[0].value = 0;
    }

    // Return object with adjusted values
    return {
        protein: Math.round(macros.find((m) => m.name === 'protein').value),
        fat: Math.round(macros.find((m) => m.name === 'fat').value),
        carbs: Math.round(macros.find((m) => m.name === 'carbs').value),
    };
};

/**
 * Validates if grams and percentage are consistent
 * @param {number} grams - Amount in grams
 * @param {number} percentage - Expected percentage
 * @param {number} totalCalories - Total calories
 * @param {number} caloriesPerGram - Calories per gram
 * @param {number} tolerance - Error tolerance in percentage (default 0.5%)
 * @returns {boolean} true if consistent, false otherwise
 */
export const isConsistent = (grams, percentage, totalCalories, caloriesPerGram, tolerance = 0.5) => {
    const calculatedPercentage = calculatePercentageFromGrams(grams, totalCalories, caloriesPerGram);
    return Math.abs(calculatedPercentage - percentage) <= tolerance;
};

/**
 * Creates macro data with standard properties
 * @private
 */
const createMacroData = (originalData, percentage, originalPercentage, wasAdjusted = false) => ({
    ...originalData,
    percentage,
    originalPercentage,
    wasAdjusted
});

/**
 * Creates the result object for macro adjustments
 * @private
 */
const createAdjustmentResult = (macroArray, adjustmentInfo = null) => {
    const result = {
        adjustmentInfo
    };
    
    macroArray.forEach(macro => {
        result[macro.name] = createMacroData(
            macro.original,
            macro.value,
            macro.originalPercentage,
            macro.wasAdjusted || false
        );
    });
    
    return result;
};

/**
 * Determines if adjustment is needed based on total
 * @private
 */
const shouldAdjust = (total) => {
    return total === ROUNDING_ERROR_TOLERANCE.MIN || 
           total === ROUNDING_ERROR_TOLERANCE.MAX;
};

/**
 * Adjusts macro percentages ONLY when necessary
 * Only adjusts when the sum is close to 100% but not exact due to rounding
 * @param {Object} proteinData - Protein data with percentage
 * @param {Object} fatData - Fat data with percentage
 * @param {Object} carbsData - Carbohydrates data with percentage
 * @returns {Object} Adjusted data {protein, fat, carbs, adjustmentInfo}
 */
export const smartAdjustMacroPercentages = (proteinData, fatData, carbsData) => {
    // Extract and round percentages
    const originalValues = {
        protein: proteinData.percentage || 0,
        fat: fatData.percentage || 0,
        carbs: carbsData.percentage || 0
    };
    
    const roundedValues = {
        protein: Math.round(originalValues.protein),
        fat: Math.round(originalValues.fat),
        carbs: Math.round(originalValues.carbs)
    };
    
    const total = roundedValues.protein + roundedValues.fat + roundedValues.carbs;
    
    // Create macro array for easier manipulation
    const macros = [
        {
            name: 'protein',
            value: roundedValues.protein,
            original: proteinData,
            originalPercentage: originalValues.protein
        },
        {
            name: 'fat',
            value: roundedValues.fat,
            original: fatData,
            originalPercentage: originalValues.fat
        },
        {
            name: 'carbs',
            value: roundedValues.carbs,
            original: carbsData,
            originalPercentage: originalValues.carbs
        }
    ];
    
    // Handle edge cases
    if (total === 0 || total === 100 || !shouldAdjust(total)) {
        return createAdjustmentResult(macros);
    }
    
    // Perform adjustment on the largest value
    const sortedMacros = [...macros].sort((a, b) => b.value - a.value);
    const diff = 100 - total;
    const adjustedMacro = sortedMacros[0];
    
    adjustedMacro.value += diff;
    adjustedMacro.wasAdjusted = true;
    
    // Create adjustment information
    const adjustmentInfo = {
        originalTotal: total,
        adjustedTotal: 100,
        adjustedMacro: adjustedMacro.name,
        adjustment: diff
    };
    
    return createAdjustmentResult(macros, adjustmentInfo);
};
